import { RATIO_FRAME_MAX_WIDTH } from "@/components/sandboxed-iframe";
import { Card, CardContent } from "@/components/ui/card";
import { useT } from "@/lib/i18n/use-t";
import type { YoutubeVideoConfig } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { T } from "./youtube-video-renderer.i18n";

type YoutubeVideoRendererProps = {
  config: YoutubeVideoConfig;
};

// Extract YouTube video ID from various URL formats
function extractYoutubeId(url: string): string | null {
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

export function YoutubeVideoRenderer({ config }: YoutubeVideoRendererProps) {
  const t = useT(T);
  const videoId = extractYoutubeId(config.url);

  if (!videoId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-destructive">{t.invalidUrl}</p>
        </CardContent>
      </Card>
    );
  }

  // Capped like the "Web page (URL)" player: at full flow width a 16:9 video fills the screen.
  return (
    <Card className={cn("mx-auto w-full", RATIO_FRAME_MAX_WIDTH)}>
      <CardContent className="p-0">
        <div className="relative aspect-video w-full">
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            title={t.videoTitle}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </CardContent>
    </Card>
  );
}
